import { Badge } from '@/components/badge';
import { Card, CardContent } from '@/components/card';
import { ExpandMoreButton } from '@/components/expand-more-button';
import TextStack, {
  TextLabel,
  TextStackItem,
  TextValue
} from '@/components/text-stack';
import { DisbursementRequestAlertDto } from '@/models/suba/alerts/disbursement-request-alert-dto.model';
import { DisbursementStatusColorMap } from '@/models/suba/distributions/DisbursementStatusColorMap.model';
import { useAlertContext } from '@/routes/suba/common/contexts/AlertContext';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import formatters from '@/utils/Formatters';
import { Collapse, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';

import { FC, useMemo } from 'react';

import { CardHeader } from '../../../common/CardHeader.component';

export const DisbursementDetails: FC = () => {
  const alert = useAlertContext<DisbursementRequestAlertDto>();

  const [isExpanded, toggleIsExpanded] = useToggle(true);

  const getDisbursementQuery = useQuery(
    ['DisbursementService.getById', alert.details?.disbursementRequestQueueId],
    () => DisbursementService.getById(alert.details.disbursementRequestQueueId),
    {
      enabled: !!alert?.details?.disbursementRequestQueueId
    }
  );

  const federalTax = useMemo(
    () =>
      isNaN(Number(getDisbursementQuery.data?.federalWithholdingPercent))
        ? 0
        : Number(getDisbursementQuery.data.federalWithholdingPercent),
    [getDisbursementQuery.data?.federalWithholdingPercent]
  );
  const stateTax = useMemo(
    () =>
      isNaN(Number(getDisbursementQuery.data?.stateWithholdingPercent))
        ? 0
        : Number(getDisbursementQuery.data.stateWithholdingPercent),
    [getDisbursementQuery.data?.stateWithholdingPercent]
  );
  const amount = useMemo(
    () =>
      isNaN(Number(getDisbursementQuery.data?.amount))
        ? 0
        : Number(getDisbursementQuery.data.amount),
    [getDisbursementQuery.data?.amount]
  );

  return (
    <Card size='small'>
      <CardHeader
        action={
          <ExpandMoreButton
            aria-label='expand details'
            data-testid='details-expand-more-button'
            expand={isExpanded}
            onClick={toggleIsExpanded}
          />
        }
        title='Details'
      />
      <Divider />
      <Collapse in={isExpanded}>
        <CardContent>
          <Stack spacing={5}>
            <Stack direction='row' justifyContent='space-between' spacing={2}>
              <Stack flex={1} spacing={1}>
                <Typography variant='subtitle2'>General</Typography>
                <TextStack direction='column'>
                  <TextStackItem>
                    <TextLabel>Status</TextLabel>

                    <TextValue>
                      <Badge
                        color={
                          DisbursementStatusColorMap[
                            getDisbursementQuery.data?.disbursementStatus
                          ]
                        }
                        maxWidth='fit-content'>
                        {formatters.displayCase(
                          getDisbursementQuery.data?.disbursementStatus
                        )}
                      </Badge>
                    </TextValue>
                  </TextStackItem>
                  <TextStackItem>
                    <TextLabel>Distribution Method</TextLabel>
                    <TextValue>
                      {formatters.displayCase(
                        getDisbursementQuery.data?.disbursementMethod
                      )}
                    </TextValue>
                  </TextStackItem>
                  <TextStackItem>
                    <TextLabel>Withdrawal Amount</TextLabel>
                    <TextValue>
                      {formatters.formatDollars(
                        getDisbursementQuery.data?.amount,
                        2
                      )}
                    </TextValue>
                  </TextStackItem>
                </TextStack>
              </Stack>
              <Divider flexItem orientation='vertical' />
              <Stack flex={1} spacing={1}></Stack>
            </Stack>
            <Stack direction='row' justifyContent='space-between' spacing={2}>
              <Stack flex={1} spacing={1}>
                <Typography variant='subtitle2'>Tax Preview</Typography>
                <TextStack direction='column'>
                  <TextStackItem>
                    <TextLabel>Gross Withdrawal</TextLabel>
                    <TextValue>
                      {formatters.formatDollars(
                        getDisbursementQuery.data?.amount
                      )}
                    </TextValue>
                  </TextStackItem>
                  <TextStackItem>
                    <TextLabel>
                      Federal Tax ({formatters.formatPercent(federalTax * 100)})
                    </TextLabel>
                    <TextValue>
                      -{formatters.formatDollars(amount * federalTax)}
                    </TextValue>
                  </TextStackItem>
                  <TextStackItem>
                    <TextLabel>
                      State Tax ({formatters.formatPercent(stateTax * 100)})
                    </TextLabel>
                    <TextValue>
                      -{formatters.formatDollars(amount * stateTax)}
                    </TextValue>
                  </TextStackItem>
                  <TextStackItem>
                    <TextLabel>All associated fees</TextLabel>
                    <TextValue>{null}</TextValue>
                  </TextStackItem>
                </TextStack>
                <Divider flexItem orientation='horizontal' />
                <TextStack direction='column'>
                  <TextStackItem>
                    <TextLabel>Net Estimated Withdrawal</TextLabel>
                    <TextValue>
                      {formatters.formatDollars(
                        amount * (1 - stateTax - federalTax)
                      )}
                    </TextValue>
                  </TextStackItem>
                </TextStack>
              </Stack>
              <Divider flexItem orientation='vertical' />
              <Stack flex={1} spacing={1}>
                <Typography variant='subtitle2'>Funding Source(s)</Typography>
                <Typography variant='caption'>
                  {formatters.displayCase(
                    getDisbursementQuery.data?.disbursementSource
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

DisbursementDetails.displayName = 'DisbursementDetails';
